import { useEffect, useState } from 'react'

import { OrgPluginsAPI } from '@makyoapp/makyo-sdk/api/Organizations'
import { PluginAdapter } from '@makyoapp/makyo-common'
import { Button } from '@makyoapp/makyo-ui'

import { toasted } from '@/lib/toast'
import { useCurrentOrgContext } from '@/context/CurrentOrgContext'

// Use the same channel name as in PluginOauth2Callback
const OAUTH_CHANNEL = 'makyo-oauth-channel'

interface OAuthSuccessData {
  access_token: string
  refresh_token: string
  expires_in: number
  label: string
  email: string
}

export default function OauthComponent({
  plugin,
  onSuccess,
  onError,
}: {
  plugin: PluginAdapter
  onSuccess: (data: OAuthSuccessData) => void
  onError?: (errorMessage?: string) => void
}) {
  const { currentOrg } = useCurrentOrgContext()
  const [loading, setLoading] = useState(false)
  const [oauthWindow, setOauthWindow] = useState<Window | null>(null)

  // Set up BroadcastChannel listener
  useEffect(() => {
    const channel = new BroadcastChannel(OAUTH_CHANNEL)

    const handleMessage = (event: MessageEvent) => {
      const { status, message, payload } = event.data

      if (status === 'success' && payload) {
        onSuccess({
          access_token: payload.access_token,
          refresh_token: payload.refresh_token || '',
          expires_in: payload.expires_in ? Number(payload.expires_in) : 0,
          label: `${payload.label} <${payload.email}>`,
          email: payload.email,
        })
        // Close the OAuth window if it's still open
        if (oauthWindow && !oauthWindow.closed) {
          oauthWindow.close()
        }
        setOauthWindow(null)
        setLoading(false)
      } else if (status === 'error') {
        toasted(message, { type: 'error' })
        console.error('OAuth error:', event.data.error)
        setLoading(false)
        // Call the error callback if provided
        if (onError) {
          onError(event.data.error || 'Authentication failed')
        }
      }
    }

    channel.onmessage = handleMessage

    return () => {
      channel.close()
    }
  }, [oauthWindow, onSuccess, onError])

  const handleAuthorizedClick = async () => {
    if (!currentOrg?.id) return

    setLoading(true)
    try {
      const res = await OrgPluginsAPI.ReqAuthorizeOrgPluginOauth2(currentOrg.id, plugin.id)
      const url = res._AuthURL
      // Open in a new window and store the reference
      const newWindow = window.open(url, '_blank')
      if (newWindow) {
        setOauthWindow(newWindow)
      } else {
        toasted('Please allow popups for this site', { type: 'error' })
        setLoading(false)
      }
    } catch (error) {
      console.error('Error authorizing OAuth2 plugin:', error)
      toasted('Error starting OAuth process', { type: 'error' })
      setLoading(false)
    }
  }

  return (
    <div className='flex items-center justify-center'>
      <Button
        onClick={handleAuthorizedClick}
        disabled={loading}
        className='w-full max-w-[500px] py-5 text-[15px] font-medium'
      >
        {loading ? (
          <span className='loading spin' />
        ) : (
          <div className='flex items-center justify-center gap-3'>
            <img
              src={plugin.icon || 'https://www.google.com/favicon.ico'}
              alt={plugin.name}
              className='w-5 h-5 object-contain'
            />
            Authorize
          </div>
        )}
      </Button>
    </div>
  )
}
